import { Container, useDisclosure } from '@chakra-ui/react';
import {
  BillGroupDunningStatusEnum,
  DunningBillGroupResponse,
} from '@monetize/types/app';
import {
  DEFAULT_PAGER,
  FilterType,
  GetListApiFilter,
  TDataTablePager,
} from '@monetize/types/common';
import {
  AppColumnDef,
  createColumnHelper,
} from '../../../components/Monetize/DataTableNew/appTable';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Link as ReactDomLink,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  useGetAllDunningProcesses,
  useGetDunningBillGroups,
  useResumeBillGroupDunning,
} from '../../../api/dunningService';
import {
  MBox,
  MFlex,
  MLink,
  MPageHeader,
  MPageLoader,
  MText,
} from '../../../components/Monetize';
import DataTableActions from '../../../components/Monetize/DataTable/MDataTableActions';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import {
  ACTION_COLUMN_KEY,
  createActionColumn,
} from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import {
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  textBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import {
  AccountLookupPopover,
  BillGroupLookupPopover,
} from '../../../components/Monetize/LookupPopover';
import { MCustomSelect } from '../../../components/Monetize/MCustomSelect/MCustomSelect';
import MEmptyDataPlaceholder from '../../../components/Monetize/MEmptyDataPlaceholder';
import { ROUTES } from '../../../constants';
import { BILL_GROUP_DUNNING_STATUS_DISPLAY } from '../../../constants/dunning';
import { useConfirmModal } from '../../../services/confirmModal';
import { buildFilterParamsRequestObject } from '../../../utils';
import { PauseDunningModal } from '../../AccountDetails/BillGroups/components/PauseDunningModal';

interface DunningBillGroup extends DunningBillGroupResponse {
  dunningProcessName: string;
  account: { name: string; id: string };
  billGroup: { name: string; id: string };
  dunningProcess: { name: string; id: string };
}

const columnHelper = createColumnHelper<DunningBillGroup>();

const dunningStatusFilterValues = getColumnFilterValuesForEnum(
  BillGroupDunningStatusEnum,
  BILL_GROUP_DUNNING_STATUS_DISPLAY,
);

const getColumns = (
  actionsTemplate: (rowData: DunningBillGroup) => React.ReactNode,
): AppColumnDef<DunningBillGroup, any>[] => [
  columnHelper.accessor('accountName', {
    ...getColHeaderWithSize('Account', 200),
    cell: ({ row }) => (
      <AccountLookupPopover
        value={row.original.account.id}
        displayLabel={row.original.account.name}
      />
    ),
    enableSorting: false,
  }),
  columnHelper.accessor('billGroupName', {
    ...getColHeaderWithSize('Bill Group', 200),
    cell: ({ row }) => (
      <BillGroupLookupPopover
        value={row.original.billGroup.id}
        displayLabel={row.original.billGroup.name}
      />
    ),
    enableSorting: false,
  }),
  columnHelper.accessor('currentStepName', {
    ...getColHeaderWithSize('Dunning Step', 180),
    cell: textBodyTemplate({ fallback: 'Blank' }),
    enableSorting: false,
  }),
  columnHelper.accessor('dunningStatus', {
    ...getColHeaderWithSize('Dunning Status', 180),
    cell: enumDisplayBodyTemplate({
      displayMap: BILL_GROUP_DUNNING_STATUS_DISPLAY,
    }),
    enableSorting: false,
    ...getColHeaderFilters('SET', {
      predefinedFilterValues: dunningStatusFilterValues,
    }),
  }),
  createActionColumn<DunningBillGroup>(actionsTemplate),
];

export const DunningManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showConfirmModal, closeModal, setModalLoading } = useConfirmModal();
  const {
    open: isPauseModalOpen,
    onOpen: onPauseModalOpen,
    onClose: onPauseModalClose,
  } = useDisclosure();

  const [selectedDunningProcessId, setSelectedDunningProcessId] = useState('');
  const [pager, setPager] = useState<TDataTablePager>(DEFAULT_PAGER);
  const [currentBillGroupId, setCurrentBillGroupId] = useState<string>('');
  const [filters, setFilters] = useState<FilterType[]>([]);
  const [filterParams, setFilterParams] = useState<GetListApiFilter>({});
  const tableRef = useRef<DataTableRef>(null);

  const {
    data: { dunningProcesses, dunningProcessOptions = [] } = {},
    isLoading: isDunningProcessesLoading,
  } = useGetAllDunningProcesses({ meta: { showErrorToast: true } });

  useEffect(() => {
    const dunningProcessIdFromUrl = searchParams.get('dunningProcessId');
    if (dunningProcessIdFromUrl && !selectedDunningProcessId) {
      setSelectedDunningProcessId(dunningProcessIdFromUrl);
    }
  }, [searchParams, selectedDunningProcessId]);

  useEffect(() => {
    setFilterParams(buildFilterParamsRequestObject(filters));
  }, [filters]);

  const {
    data: billGroupsResponse,
    isLoading: isBillGroupsLoading,
    refetch: refetchBillGroups,
  } = useGetDunningBillGroups({
    dunningProcessId: selectedDunningProcessId,
    pager,
    filters: filterParams,
    options: {
      meta: { showErrorToast: true },
    },
  });

  const resumeDunning = useResumeBillGroupDunning({
    meta: { showErrorToast: true },
    onSuccess: () => {
      refetchBillGroups();
      closeModal();
      setModalLoading(false);
    },
  });

  const billGroups: DunningBillGroup[] = useMemo(() => {
    return (billGroupsResponse?.content || []).map((bg) => ({
      ...bg,
      dunningProcessName:
        dunningProcesses?.find(
          (dunningProcess) => dunningProcess.id === bg.dunningProcessId,
        )?.name || '',
      account: {
        name: bg.accountName,
        id: bg.accountId,
      },
      billGroup: {
        name: bg.billGroupName,
        id: bg.billGroupId,
      },
      dunningProcess: {
        name:
          dunningProcesses?.find(
            (dunningProcess) => dunningProcess.id === bg.dunningProcessId,
          )?.name ?? '',
        id: bg.dunningProcessId,
      },
    }));
  }, [billGroupsResponse?.content, dunningProcesses]);

  const totalRecords = billGroupsResponse?.totalElements || 0;

  const handleDunningProcessChange = (value: string | null) => {
    const newValue = value ?? '';
    setSelectedDunningProcessId(newValue);
    setPager({ ...pager, page: 0 });

    const newSearchParams = new URLSearchParams(searchParams);
    if (newValue) {
      newSearchParams.set('dunningProcessId', newValue);
    } else {
      newSearchParams.delete('dunningProcessId');
    }
    navigate(`?${newSearchParams.toString()}`, { replace: true });
  };

  const onResetFilter = () => {
    setFilters([]);
  };

  const handlePauseModalClose = () => {
    onPauseModalClose();
    refetchBillGroups();
  };

  const handlePauseDunning = (billGroup: DunningBillGroup) => {
    setCurrentBillGroupId(billGroup.billGroupId);
    onPauseModalOpen();
  };

  const handleResumeDunning = (billGroup: DunningBillGroup) => {
    showConfirmModal({
      title: 'Resume Dunning',
      description:
        'Resuming dunning will restart automated past-due actions for this bill group.',
      onYes: () => {
        setModalLoading(true);
        resumeDunning.mutateAsync({
          billGroupId: billGroup.billGroupId,
        });
      },
    });
  };

  const actionsTemplate = (billGroup: DunningBillGroup) => {
    const actions = [
      {
        title: 'Pause',
        enabled: true,
        isDisabled:
          billGroup.dunningStatus !== BillGroupDunningStatusEnum.IN_DUNNING,
        action: () => handlePauseDunning(billGroup),
      },
      {
        title: 'Resume',
        enabled: true,
        isDisabled:
          billGroup.dunningStatus !== BillGroupDunningStatusEnum.PAUSED,
        action: () => handleResumeDunning(billGroup),
      },
    ];

    return <DataTableActions actions={actions} />;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => getColumns(actionsTemplate), []);

  const handleNewTableFiltersChange = (newFilters: FilterType[]) => {
    setFilters(newFilters);
  };

  if (isDunningProcessesLoading) {
    return <MPageLoader height="50vh" />;
  }

  if (!dunningProcesses?.length) {
    return (
      <Container
        maxWidth="unset"
        px="0"
        position="relative"
        centerContent
        data-testid="dunning-management-page"
      >
        <MPageHeader
          title="Dunning Management"
          subTitle={<MText>Manage dunning processes for bill groups.</MText>}
        />
        <MEmptyDataPlaceholder
          mainMessage="No dunning processes configured"
          smallMessage="Set up dunning processes in Settings to manage automated past-due actions."
          btnLabel="Go to Dunning Settings"
          to={ROUTES.SETTINGS_DUNNING_LIST}
        />
      </Container>
    );
  }

  return (
    <Container
      maxWidth="unset"
      px="0"
      position="relative"
      centerContent
      data-testid="dunning-management-page"
    >
      <MPageHeader
        title="Dunning Management"
        subTitle={<MText>Manage dunning processes for bill groups.</MText>}
      />
      <MFlex mb={4} w="100%" justifyContent="space-between" alignItems="center">
        <MBox w="300px">
          <MText mb={2} fontWeight="semibold">
            Dunning Process
          </MText>
          <MCustomSelect
            placeholder="Select a dunning process"
            value={selectedDunningProcessId}
            onChange={handleDunningProcessChange}
            items={dunningProcessOptions}
            itemTitle="label"
            itemValue="value"
            mb={2}
            clearable
          />
          {selectedDunningProcessId && (
            <MLink
              asChild
              fontWeight="400"
              fontSize="sm"
              whiteSpace="normal"
              _hover={{ textDecoration: 'underline' }}
            >
              <ReactDomLink
                to={{
                  pathname: ROUTES.getDunningEditRoute(
                    selectedDunningProcessId,
                  ),
                }}
              >
                {selectedDunningProcessId}
              </ReactDomLink>
            </MLink>
          )}
        </MBox>
      </MFlex>

      {selectedDunningProcessId ? (
        <DataTable
          ref={tableRef}
          columns={columns}
          data={billGroups}
          isLoading={isBillGroupsLoading}
          enablePagination
          pinnedRightColumns={[ACTION_COLUMN_KEY]}
          totalElements={totalRecords}
          totalPages={billGroupsResponse?.totalPages}
          pager={pager}
          setPager={setPager}
          onFiltersChange={handleNewTableFiltersChange}
          resetFilter={onResetFilter}
          emptyProps={{
            mainMessage: 'No bill groups found',
            smallMessage:
              'There are no bill groups assigned to this dunning process.',
          }}
        />
      ) : (
        <MEmptyDataPlaceholder
          mainMessage="Select a dunning process"
          smallMessage="Choose a dunning process from the dropdown above to view its assigned bill groups."
        />
      )}
      {isPauseModalOpen && (
        <PauseDunningModal
          isOpen
          onClose={handlePauseModalClose}
          billGroupId={currentBillGroupId}
        />
      )}
    </Container>
  );
};
